import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Scale,
  ArrowLeftRight,
  BookOpen,
  Receipt,
  Wallet,
  Clock,
  Download,
  FileText,
  RefreshCw,
  Search,
} from "lucide-react";
import { apiFetch, API } from "@/utils/api";
import {
  HIDDEN_METRIC_KEYS,
  labelForMetricKey,
  formatMetricValue,
  formatTensionMetric,
  severityLabel,
} from "@/utils/monthlyReportFormat";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function formatCurrency(amount) {
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(amount);
}

function formatPercent(value) {
  if (value == null || !isFinite(value)) return "-";
  return new Intl.NumberFormat("es-ES", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function LoadingSpinner() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="spinner" />
    </div>
  );
}

function EmptyState({ message = "Sin datos disponibles", colSpan = 2 }) {
  return (
    <TableRow>
      <TableCell colSpan={colSpan} className="text-center text-slate-500 py-8">
        {message}
      </TableCell>
    </TableRow>
  );
}

function ExcelDownloadButton({ onClick }) {
  return (
    <Button
      variant="outline"
      size="sm"
      className="gap-2"
      onClick={onClick || (() => toast.info("Proximamente"))}
    >
      <Download className="w-4 h-4" />
      Descargar Excel
    </Button>
  );
}

function variationColor(value) {
  if (value > 0) return "text-emerald-700";
  if (value < 0) return "text-rose-700";
  return "text-slate-500";
}

// ---------------------------------------------------------------------------
// 1. Balance Sheet (existing)
// ---------------------------------------------------------------------------

function BalanceReport({ data, loading }) {
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExcelDownloadButton />
      </div>
      <div className="grid md:grid-cols-2 gap-6">
        {/* Assets */}
        <Card>
          <CardHeader className="pb-3 bg-blue-50">
            <CardTitle className="text-lg font-semibold text-blue-900">
              Activo
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Cuenta</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.assets.accounts.map((acc) => (
                  <TableRow key={acc.account_id}>
                    <TableCell>
                      <span className="font-mono text-blue-600 mr-2">{acc.code}</span>
                      {acc.name}
                    </TableCell>
                    <TableCell className="numeric">{formatCurrency(acc.balance)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-blue-50 font-medium">
                  <TableCell>Total Activo</TableCell>
                  <TableCell className="numeric text-lg">{formatCurrency(data.assets.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Liabilities + Equity */}
        <Card>
          <CardHeader className="pb-3 bg-rose-50">
            <CardTitle className="text-lg font-semibold text-rose-900">
              Pasivo y Patrimonio
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Cuenta</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.liabilities.accounts.map((acc) => (
                  <TableRow key={acc.account_id}>
                    <TableCell>
                      <span className="font-mono text-rose-600 mr-2">{acc.code}</span>
                      {acc.name}
                    </TableCell>
                    <TableCell className="numeric">{formatCurrency(Math.abs(acc.balance))}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-slate-50">
                  <TableCell className="font-medium">Subtotal Pasivo</TableCell>
                  <TableCell className="numeric">{formatCurrency(Math.abs(data.liabilities.total))}</TableCell>
                </TableRow>
                {data.equity.accounts.map((acc) => (
                  <TableRow key={acc.account_id}>
                    <TableCell>
                      <span className="font-mono text-purple-600 mr-2">{acc.code}</span>
                      {acc.name}
                    </TableCell>
                    <TableCell className="numeric">{formatCurrency(Math.abs(acc.balance))}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-rose-50 font-medium">
                  <TableCell>Total Pasivo + Patrimonio</TableCell>
                  <TableCell className="numeric text-lg">{formatCurrency(Math.abs(data.total_liabilities_equity))}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. Profit & Loss (existing)
// ---------------------------------------------------------------------------

function PLReport({ data, loading }) {
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExcelDownloadButton />
      </div>
      <div className="space-y-6">
        {/* Income */}
        <Card>
          <CardHeader className="pb-3 bg-emerald-50">
            <CardTitle className="text-lg font-semibold text-emerald-900 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Ingresos
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Cuenta</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.income.items.length === 0 ? (
                  <EmptyState message="Sin ingresos en este periodo" />
                ) : (
                  data.income.items.map((item) => (
                    <TableRow key={item.code}>
                      <TableCell>
                        <span className="font-mono text-emerald-600 mr-2">{item.code}</span>
                        {item.name}
                      </TableCell>
                      <TableCell className="numeric positive">{formatCurrency(item.amount)}</TableCell>
                    </TableRow>
                  ))
                )}
                <TableRow className="bg-emerald-50 font-medium">
                  <TableCell>Total Ingresos</TableCell>
                  <TableCell className="numeric text-lg text-emerald-700">{formatCurrency(data.income.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Expenses */}
        <Card>
          <CardHeader className="pb-3 bg-rose-50">
            <CardTitle className="text-lg font-semibold text-rose-900 flex items-center gap-2">
              <TrendingDown className="w-5 h-5" />
              Gastos
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Cuenta</TableHead>
                  <TableHead className="text-right">Importe</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.expenses.items.length === 0 ? (
                  <EmptyState message="Sin gastos en este periodo" />
                ) : (
                  data.expenses.items.map((item) => (
                    <TableRow key={item.code}>
                      <TableCell>
                        <span className="font-mono text-rose-600 mr-2">{item.code}</span>
                        {item.name}
                      </TableCell>
                      <TableCell className="numeric negative">{formatCurrency(item.amount)}</TableCell>
                    </TableRow>
                  ))
                )}
                <TableRow className="bg-rose-50 font-medium">
                  <TableCell>Total Gastos</TableCell>
                  <TableCell className="numeric text-lg text-rose-700">{formatCurrency(data.expenses.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Net Profit */}
        <Card className={data.net_profit >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold">Resultado del Ejercicio</span>
              <span className={`font-mono text-2xl font-bold ${data.net_profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                {formatCurrency(data.net_profit)}
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Trial Balance (existing)
// ---------------------------------------------------------------------------

function TrialBalanceReport({ data, loading }) {
  if (loading) return <LoadingSpinner />;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <ExcelDownloadButton />
      </div>
      <Card>
        <CardContent className="p-0">
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Cuenta</TableHead>
                <TableHead>Nombre</TableHead>
                <TableHead className="text-right">Debe</TableHead>
                <TableHead className="text-right">Haber</TableHead>
                <TableHead className="text-right">Saldo Deudor</TableHead>
                <TableHead className="text-right">Saldo Acreedor</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.accounts.length === 0 ? (
                <EmptyState message="Sin movimientos en este periodo" colSpan={6} />
              ) : (
                data.accounts.map((acc) => (
                  <TableRow key={acc.code}>
                    <TableCell className="font-mono text-blue-600">{acc.code}</TableCell>
                    <TableCell>{acc.name}</TableCell>
                    <TableCell className="numeric">{formatCurrency(acc.debit)}</TableCell>
                    <TableCell className="numeric">{formatCurrency(acc.credit)}</TableCell>
                    <TableCell className="numeric">
                      {acc.debit_balance > 0 ? formatCurrency(acc.debit_balance) : ""}
                    </TableCell>
                    <TableCell className="numeric">
                      {acc.credit_balance > 0 ? formatCurrency(acc.credit_balance) : ""}
                    </TableCell>
                  </TableRow>
                ))
              )}
              <TableRow className="bg-slate-100 font-medium">
                <TableCell colSpan={2}>Totales</TableCell>
                <TableCell className="numeric">{formatCurrency(data.totals.debit)}</TableCell>
                <TableCell className="numeric">{formatCurrency(data.totals.credit)}</TableCell>
                <TableCell colSpan={2} />
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 4. Comparative Report (new)
// ---------------------------------------------------------------------------

function currentPeriodFor(granularity, defaultPeriod) {
  if (granularity === "month") return defaultPeriod;
  const d = new Date(defaultPeriod + "-01");
  if (granularity === "quarter") return `${d.getFullYear()}-Q${Math.ceil((d.getMonth() + 1) / 3)}`;
  return String(d.getFullYear());
}

function previousPeriod(period) {
  // "YYYY-MM" -> mes anterior; "YYYY-Qn" -> trimestre anterior; "YYYY" -> anio anterior
  const quarterMatch = period.match(/^(\d{4})-Q([1-4])$/);
  if (quarterMatch) {
    const [, y, q] = quarterMatch;
    return q === "1" ? `${Number(y) - 1}-Q4` : `${y}-Q${Number(q) - 1}`;
  }
  if (/^\d{4}-\d{2}$/.test(period)) {
    const d = new Date(period + "-01");
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  }
  return String(Number(period) - 1);
}

function PeriodPicker({ granularity, value, onChange }) {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear - 5; y <= currentYear + 1; y++) years.push(String(y));

  if (granularity === "month") {
    return <Input type="month" value={value} onChange={(e) => onChange(e.target.value)} />;
  }
  if (granularity === "year") {
    return (
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger><SelectValue /></SelectTrigger>
        <SelectContent>
          {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  // quarter: value "YYYY-Qn"
  const [year = String(currentYear), quarter = "1"] = (value.match(/^(\d{4})-Q([1-4])$/) || []).slice(1);
  return (
    <div className="flex gap-2">
      <Select value={year} onValueChange={(y) => onChange(`${y}-Q${quarter}`)}>
        <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
        <SelectContent>
          {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={quarter} onValueChange={(q) => onChange(`${year}-Q${q}`)}>
        <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
        <SelectContent>
          {["1", "2", "3", "4"].map((q) => <SelectItem key={q} value={q}>{`T${q}`}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function ComparativeSummaryCard({ title, p1, p2, invertColors }) {
  const variation = (p1 ?? 0) - (p2 ?? 0);
  // En gastos, subir es malo: se invierte el color
  const colorValue = invertColors ? -variation : variation;
  return (
    <Card>
      <CardContent className="p-5">
        <p className="text-sm font-medium text-slate-600 mb-1">{title}</p>
        <p className="font-mono text-xl font-bold text-slate-900">{formatCurrency(p1 ?? 0)}</p>
        <p className="text-xs text-slate-500 mt-1">
          antes {formatCurrency(p2 ?? 0)} ·{" "}
          <span className={`font-medium ${variationColor(colorValue)}`}>
            {variation >= 0 ? "+" : ""}{formatCurrency(variation)}
          </span>
        </p>
      </CardContent>
    </Card>
  );
}

function ComparativeReport({ defaultPeriod }) {
  const [granularity, setGranularity] = useState("month");
  const [period1, setPeriod1] = useState(defaultPeriod);
  const [period2, setPeriod2] = useState(() => previousPeriod(defaultPeriod));
  const [reportType, setReportType] = useState("profit-loss");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(
        `${API}/reports/comparative?report_type=${reportType}&period1=${period1}&period2=${period2}`
      );
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || "Error al cargar comparativa");
        toast.error(err.detail || "Error al cargar comparativa");
      }
    } catch (e) {
      setError("Error de conexion");
      toast.error("Error al cargar comparativa");
    } finally {
      setLoading(false);
    }
  }, [period1, period2, reportType]);

  const changeGranularity = (g) => {
    setGranularity(g);
    const p1 = currentPeriodFor(g, defaultPeriod);
    setPeriod1(p1);
    setPeriod2(previousPeriod(p1));
    setData(null);
  };

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-40">
              <Label className="mb-2 block">Comparar por</Label>
              <Select value={granularity} onValueChange={changeGranularity}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Mes</SelectItem>
                  <SelectItem value="quarter">Trimestre</SelectItem>
                  <SelectItem value="year">Anio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Periodo</Label>
              <PeriodPicker granularity={granularity} value={period1} onChange={setPeriod1} />
            </div>
            <div>
              <Label className="mb-2 block">Comparado con</Label>
              <PeriodPicker granularity={granularity} value={period2} onChange={setPeriod2} />
            </div>
            <div className="w-52">
              <Label className="mb-2 block">Tipo de informe</Label>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="profit-loss">Perdidas y Ganancias</SelectItem>
                  <SelectItem value="balance">Balance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchData} disabled={loading}>
              {loading ? "Cargando..." : "Comparar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <ExcelDownloadButton />
      </div>

      {/* Summary: ingresos / gastos / resultado con variacion */}
      {data?.summary && !loading && (
        <div className="grid sm:grid-cols-3 gap-4">
          <ComparativeSummaryCard
            title="Ingresos"
            p1={data.summary.period1.income}
            p2={data.summary.period2.income}
          />
          <ComparativeSummaryCard
            title="Gastos"
            p1={data.summary.period1.expense}
            p2={data.summary.period2.expense}
            invertColors
          />
          <ComparativeSummaryCard
            title="Resultado"
            p1={data.summary.period1.net}
            p2={data.summary.period2.net}
          />
        </div>
      )}

      {/* Results */}
      {loading && <LoadingSpinner />}
      {error && !loading && (
        <Card>
          <CardContent className="p-6 text-center text-rose-600">{error}</CardContent>
        </Card>
      )}
      {data && !loading && (
        <Card>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Cuenta</TableHead>
                  <TableHead>Nombre</TableHead>
                  <TableHead className="text-right">
                    {period1}
                  </TableHead>
                  <TableHead className="text-right">
                    {period2}
                  </TableHead>
                  <TableHead className="text-right">Variacion (EUR)</TableHead>
                  <TableHead className="text-right">Variacion (%)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data.items || data.items.length === 0) ? (
                  <EmptyState message="Sin datos para comparar" colSpan={6} />
                ) : (
                  data.items.map((item) => {
                    const variation = (item.period1_amount ?? 0) - (item.period2_amount ?? 0);
                    const pct = item.variation_percent ?? (
                      item.period2_amount ? ((variation / Math.abs(item.period2_amount)) * 100) : null
                    );
                    return (
                      <TableRow key={item.code}>
                        <TableCell className="font-mono text-blue-600">{item.code}</TableCell>
                        <TableCell>{item.name}</TableCell>
                        <TableCell className="numeric">{formatCurrency(item.period1_amount ?? 0)}</TableCell>
                        <TableCell className="numeric">{formatCurrency(item.period2_amount ?? 0)}</TableCell>
                        <TableCell className={`numeric font-medium ${variationColor(item.variation ?? variation)}`}>
                          {formatCurrency(item.variation ?? variation)}
                        </TableCell>
                        <TableCell className={`numeric font-medium ${variationColor(item.variation ?? variation)}`}>
                          {formatPercent(item.variation_percent ?? pct)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 5. Ledger (Libro Mayor) (new)
// ---------------------------------------------------------------------------

function LedgerReport({ defaultPeriod }) {
  const [accountSearch, setAccountSearch] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState("");
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch accounts for the searchable selector
  useEffect(() => {
    let cancelled = false;
    const fetchAccounts = async () => {
      setSearchLoading(true);
      try {
        const res = await apiFetch(`${API}/accounting/chart`);
        if (res.ok) {
          const json = await res.json();
          if (!cancelled) setAccounts(json.accounts || json || []);
        }
      } catch {
        // silently fail — accounts won't load
      } finally {
        if (!cancelled) setSearchLoading(false);
      }
    };
    fetchAccounts();
    return () => { cancelled = true; };
  }, []);

  const filteredAccounts = useMemo(() => {
    if (!accountSearch) return accounts.slice(0, 20);
    const q = accountSearch.toLowerCase();
    return accounts
      .filter((a) => a.code?.toLowerCase().includes(q) || a.name?.toLowerCase().includes(q))
      .slice(0, 20);
  }, [accounts, accountSearch]);

  const fetchLedger = useCallback(async () => {
    if (!selectedAccount) {
      toast.warning("Selecciona una cuenta primero");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(
        `${API}/accounting/ledger/${selectedAccount}?period=${period}`
      );
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || "Error al cargar libro mayor");
        toast.error(err.detail || "Error al cargar libro mayor");
      }
    } catch {
      setError("Error de conexion");
      toast.error("Error al cargar libro mayor");
    } finally {
      setLoading(false);
    }
  }, [selectedAccount, period]);

  const selectedAccountObj = accounts.find((a) => a.code === selectedAccount);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-72">
              <Label className="mb-2 block">Cuenta</Label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Buscar cuenta..."
                  value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              {(accountSearch || !selectedAccount) && filteredAccounts.length > 0 && (
                <div className="mt-1 border rounded-md bg-white shadow-md max-h-48 overflow-y-auto z-10 relative">
                  {filteredAccounts.map((acc) => (
                    <button
                      key={acc.code}
                      type="button"
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-100 flex gap-2 ${
                        selectedAccount === acc.code ? "bg-blue-50 text-blue-700" : ""
                      }`}
                      onClick={() => {
                        setSelectedAccount(acc.code);
                        setAccountSearch(`${acc.code} - ${acc.name}`);
                      }}
                    >
                      <span className="font-mono text-blue-600">{acc.code}</span>
                      <span className="truncate">{acc.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="w-44">
              <Label className="mb-2 block">Periodo</Label>
              <Input
                type="month"
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
              />
            </div>
            <Button onClick={fetchLedger} disabled={loading || !selectedAccount}>
              {loading ? "Cargando..." : "Consultar"}
            </Button>
          </div>
          {selectedAccountObj && (
            <p className="text-sm text-slate-500 mt-2">
              Cuenta seleccionada: <span className="font-mono font-medium text-blue-600">{selectedAccountObj.code}</span> - {selectedAccountObj.name}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <ExcelDownloadButton />
      </div>

      {/* Results */}
      {loading && <LoadingSpinner />}
      {error && !loading && (
        <Card>
          <CardContent className="p-6 text-center text-rose-600">{error}</CardContent>
        </Card>
      )}
      {data && !loading && (
        <Card>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Asiento</TableHead>
                  <TableHead>Descripcion</TableHead>
                  <TableHead className="text-right">Debe</TableHead>
                  <TableHead className="text-right">Haber</TableHead>
                  <TableHead className="text-right">Saldo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.opening_balance != null && (
                  <TableRow className="bg-slate-50">
                    <TableCell colSpan={5} className="text-slate-600 italic">
                      Saldo inicial del periodo (arrastre de periodos anteriores)
                    </TableCell>
                    <TableCell className={`numeric font-medium ${data.opening_balance >= 0 ? "text-blue-700" : "text-rose-700"}`}>
                      {formatCurrency(data.opening_balance)}
                    </TableCell>
                  </TableRow>
                )}
                {(!data.entries || data.entries.length === 0) ? (
                  <EmptyState message="Sin movimientos en este periodo" colSpan={6} />
                ) : (
                  (() => {
                    let runningBalance = data.opening_balance ?? 0;
                    return data.entries.map((entry, idx) => {
                      runningBalance += (entry.debit ?? 0) - (entry.credit ?? 0);
                      const balance = entry.running_balance ?? runningBalance;
                      return (
                        <TableRow key={entry.id || idx}>
                          <TableCell className="whitespace-nowrap">{formatDate(entry.date)}</TableCell>
                          <TableCell className="font-mono text-sm">{entry.entry_number || entry.journal_id || "-"}</TableCell>
                          <TableCell className="max-w-xs truncate">{entry.description || ""}</TableCell>
                          <TableCell className="numeric">{entry.debit ? formatCurrency(entry.debit) : ""}</TableCell>
                          <TableCell className="numeric">{entry.credit ? formatCurrency(entry.credit) : ""}</TableCell>
                          <TableCell className={`numeric font-medium ${balance >= 0 ? "text-blue-700" : "text-rose-700"}`}>
                            {formatCurrency(balance)}
                          </TableCell>
                        </TableRow>
                      );
                    });
                  })()
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 6. VAT Summary (IVA Trimestral) (new)
// ---------------------------------------------------------------------------

function VATReport() {
  const currentYear = new Date().getFullYear();
  const currentQuarter = Math.ceil((new Date().getMonth() + 1) / 3);
  const [mode, setMode] = useState("quarter"); // quarter | range
  const [year, setYear] = useState(String(currentYear));
  const [quarter, setQuarter] = useState(String(currentQuarter));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (mode === "range" && (!dateFrom || !dateTo)) {
      toast.warning("Indica las dos fechas del rango");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const url = mode === "range"
        ? `${API}/reports/vat-summary?date_from=${dateFrom}&date_to=${dateTo}`
        : `${API}/reports/vat-summary?year=${year}&quarter=${quarter}`;
      const res = await apiFetch(url);
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || "Error al cargar resumen de IVA");
        toast.error(err.detail || "Error al cargar resumen de IVA");
      }
    } catch {
      setError("Error de conexion");
      toast.error("Error al cargar resumen de IVA");
    } finally {
      setLoading(false);
    }
  }, [mode, year, quarter, dateFrom, dateTo]);

  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear - 3; y <= currentYear + 1; y++) arr.push(String(y));
    return arr;
  }, [currentYear]);

  function VATSection({ title, items, colorClass, bgClass }) {
    if (!items || items.length === 0) return null;
    const total = items.reduce((sum, i) => sum + (i.amount ?? 0), 0);
    return (
      <Card>
        <CardHeader className={`pb-3 ${bgClass}`}>
          <CardTitle className={`text-base font-semibold ${colorClass}`}>{title}</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table className="table-dense">
            <TableHeader>
              <TableRow>
                <TableHead>Concepto</TableHead>
                <TableHead className="text-right">Base imponible</TableHead>
                <TableHead className="text-right">Tipo</TableHead>
                <TableHead className="text-right">Cuota</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.description || item.concept || "-"}</TableCell>
                  <TableCell className="numeric">{formatCurrency(item.tax_base ?? 0)}</TableCell>
                  <TableCell className="numeric">{item.rate != null ? `${item.rate}%` : "-"}</TableCell>
                  <TableCell className="numeric">{formatCurrency(item.amount ?? 0)}</TableCell>
                </TableRow>
              ))}
              <TableRow className={`font-medium ${bgClass}`}>
                <TableCell colSpan={3}>Total</TableCell>
                <TableCell className="numeric">{formatCurrency(total)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-40">
              <Label className="mb-2 block">Periodo</Label>
              <Select value={mode} onValueChange={setMode}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="quarter">Trimestre</SelectItem>
                  <SelectItem value="range">Rango de fechas</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {mode === "quarter" ? (
              <>
                <div className="w-36">
                  <Label className="mb-2 block">Ejercicio</Label>
                  <Select value={year} onValueChange={setYear}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map((y) => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="w-36">
                  <Label className="mb-2 block">Trimestre</Label>
                  <Select value={quarter} onValueChange={setQuarter}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">T1 (Ene-Mar)</SelectItem>
                      <SelectItem value="2">T2 (Abr-Jun)</SelectItem>
                      <SelectItem value="3">T3 (Jul-Sep)</SelectItem>
                      <SelectItem value="4">T4 (Oct-Dic)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            ) : (
              <>
                <div className="w-44">
                  <Label className="mb-2 block">Desde</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </div>
                <div className="w-44">
                  <Label className="mb-2 block">Hasta</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </div>
              </>
            )}
            <Button onClick={fetchData} disabled={loading}>
              {loading ? "Cargando..." : "Calcular IVA"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <ExcelDownloadButton />
      </div>

      {/* Results */}
      {loading && <LoadingSpinner />}
      {error && !loading && (
        <Card>
          <CardContent className="p-6 text-center text-rose-600">{error}</CardContent>
        </Card>
      )}
      {data && !loading && (
        <div className="space-y-4">
          <VATSection
            title="IVA Repercutido"
            items={data.output_vat || data.iva_repercutido}
            colorClass="text-emerald-900"
            bgClass="bg-emerald-50"
          />
          <VATSection
            title="IVA Soportado Nacional"
            items={data.input_vat_domestic || data.iva_soportado_nacional}
            colorClass="text-blue-900"
            bgClass="bg-blue-50"
          />
          <VATSection
            title="IVA Soportado Intracomunitario"
            items={data.input_vat_eu || data.iva_soportado_intracomunitario}
            colorClass="text-indigo-900"
            bgClass="bg-indigo-50"
          />
          <VATSection
            title="IVA Soportado Extracomunitario"
            items={data.input_vat_non_eu || data.iva_soportado_extracomunitario}
            colorClass="text-purple-900"
            bgClass="bg-purple-50"
          />

          {/* IVA perdido: no deducible, fuera del resultado */}
          {data.iva_perdido?.items?.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-3 bg-amber-50">
                <CardTitle className="text-base font-semibold text-amber-900">
                  IVA no deducible (perdido)
                </CardTitle>
                <p className="text-xs text-amber-800 mt-1">
                  IVA cobrado por el proveedor que no es deducible: es coste, no entra en la liquidacion.
                  Las facturas en divisa sin conciliar no suman al total EUR.
                </p>
              </CardHeader>
              <CardContent className="p-0">
                <Table className="table-dense">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Proveedor</TableHead>
                      <TableHead>Factura</TableHead>
                      <TableHead className="text-right">Cuota</TableHead>
                      <TableHead className="text-right">EUR real</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.iva_perdido.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.supplier_name || "-"}</TableCell>
                        <TableCell className="font-mono text-sm">{item.invoice_number}</TableCell>
                        <TableCell className="numeric">
                          {item.currency === "EUR"
                            ? formatCurrency(item.vat_amount)
                            : `${item.vat_amount.toLocaleString("es-ES")} ${item.currency}`}
                        </TableCell>
                        <TableCell className="numeric">
                          {item.pending_conciliation ? (
                            <Badge variant="outline" className="bg-amber-100 text-amber-800">
                              sin conciliar
                            </Badge>
                          ) : (
                            formatCurrency(item.vat_eur)
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-amber-50 font-medium">
                      <TableCell colSpan={3}>Total (solo importes con EUR real)</TableCell>
                      <TableCell className="numeric">{formatCurrency(data.iva_perdido.total_eur)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Result */}
          {data.result != null && (
            <Card className={data.result >= 0 ? "bg-rose-50 border-rose-200" : "bg-emerald-50 border-emerald-200"}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-lg font-semibold">Resultado liquidacion</span>
                    <Badge
                      className={`ml-3 ${data.result >= 0 ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}
                      variant="outline"
                    >
                      {data.result >= 0 ? "A pagar" : "A devolver"}
                    </Badge>
                  </div>
                  <span className={`font-mono text-2xl font-bold ${data.result >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                    {formatCurrency(Math.abs(data.result))}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 7. Cash Flow (new)
// ---------------------------------------------------------------------------

function CashFlowReport() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(String(currentYear));
  const [month, setMonth] = useState("_all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const years = useMemo(() => {
    const arr = [];
    for (let y = currentYear - 3; y <= currentYear + 1; y++) arr.push(String(y));
    return arr;
  }, [currentYear]);

  const months = [
    { value: "_all", label: "Todos" },
    { value: "1", label: "Enero" },
    { value: "2", label: "Febrero" },
    { value: "3", label: "Marzo" },
    { value: "4", label: "Abril" },
    { value: "5", label: "Mayo" },
    { value: "6", label: "Junio" },
    { value: "7", label: "Julio" },
    { value: "8", label: "Agosto" },
    { value: "9", label: "Septiembre" },
    { value: "10", label: "Octubre" },
    { value: "11", label: "Noviembre" },
    { value: "12", label: "Diciembre" },
  ];

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = `${API}/reports/cash-flow?year=${year}`;
      if (month && month !== "_all") url += `&month=${month}`;
      const res = await apiFetch(url);
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || "Error al cargar cash flow");
        toast.error(err.detail || "Error al cargar cash flow");
      }
    } catch {
      setError("Error de conexion");
      toast.error("Error al cargar cash flow");
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  function FlowCard({ title, amount, icon: Icon, colorClass, bgClass }) {
    return (
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${bgClass}`}>
                <Icon className={`w-5 h-5 ${colorClass}`} />
              </div>
              <span className="text-sm font-medium text-slate-600">{title}</span>
            </div>
            <span className={`font-mono text-lg font-bold ${colorClass}`}>
              {formatCurrency(amount ?? 0)}
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-36">
              <Label className="mb-2 block">Ejercicio</Label>
              <Select value={year} onValueChange={setYear}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={y}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="w-44">
              <Label className="mb-2 block">Mes (opcional)</Label>
              <Select value={month} onValueChange={setMonth}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  {months.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchData} disabled={loading}>
              {loading ? "Cargando..." : "Generar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <ExcelDownloadButton />
      </div>

      {/* Results */}
      {loading && <LoadingSpinner />}
      {error && !loading && (
        <Card>
          <CardContent className="p-6 text-center text-rose-600">{error}</CardContent>
        </Card>
      )}
      {data && !loading && (
        <div className="space-y-6">
          {/* Flow cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <FlowCard
              title="Cobros clientes"
              amount={data.customer_collections ?? data.cobros_clientes}
              icon={TrendingUp}
              colorClass="text-emerald-700"
              bgClass="bg-emerald-100"
            />
            <FlowCard
              title="Pagos proveedores"
              amount={data.supplier_payments ?? data.pagos_proveedores}
              icon={TrendingDown}
              colorClass="text-rose-700"
              bgClass="bg-rose-100"
            />
            <FlowCard
              title="Otros ingresos"
              amount={data.other_income ?? data.otros_ingresos}
              icon={Wallet}
              colorClass="text-blue-700"
              bgClass="bg-blue-100"
            />
            <FlowCard
              title="Otros gastos"
              amount={data.other_expenses ?? data.otros_gastos}
              icon={Wallet}
              colorClass="text-amber-700"
              bgClass="bg-amber-100"
            />
          </div>

          {/* Summary */}
          <div className="grid sm:grid-cols-3 gap-4">
            <Card className="bg-emerald-50 border-emerald-200">
              <CardContent className="p-5 text-center">
                <p className="text-sm text-emerald-600 mb-1">Total entradas</p>
                <p className="font-mono text-xl font-bold text-emerald-700">
                  {formatCurrency(data.total_inflows ?? data.total_entradas ?? 0)}
                </p>
              </CardContent>
            </Card>
            <Card className="bg-rose-50 border-rose-200">
              <CardContent className="p-5 text-center">
                <p className="text-sm text-rose-600 mb-1">Total salidas</p>
                <p className="font-mono text-xl font-bold text-rose-700">
                  {formatCurrency(data.total_outflows ?? data.total_salidas ?? 0)}
                </p>
              </CardContent>
            </Card>
            <Card className={
              (data.net_flow ?? data.flujo_neto ?? 0) >= 0
                ? "bg-emerald-50 border-emerald-200"
                : "bg-rose-50 border-rose-200"
            }>
              <CardContent className="p-5 text-center">
                <p className="text-sm text-slate-600 mb-1">Flujo neto</p>
                <p className={`font-mono text-xl font-bold ${
                  (data.net_flow ?? data.flujo_neto ?? 0) >= 0 ? "text-emerald-700" : "text-rose-700"
                }`}>
                  {formatCurrency(data.net_flow ?? data.flujo_neto ?? 0)}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Serie mensual: neto y saldo acumulado (mismas cifras que la pestana Real) */}
          {data.months?.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold">Mes a mes</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table className="table-dense">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mes</TableHead>
                      <TableHead className="text-right">Entradas</TableHead>
                      <TableHead className="text-right">Salidas</TableHead>
                      <TableHead className="text-right">Neto</TableHead>
                      <TableHead className="text-right">Saldo acumulado</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.months.map((m) => (
                      <TableRow key={m.month}>
                        <TableCell className="font-mono text-sm">{m.month}</TableCell>
                        <TableCell className="numeric">{formatCurrency(m.inflows)}</TableCell>
                        <TableCell className="numeric">{formatCurrency(m.outflows)}</TableCell>
                        <TableCell className={`numeric font-medium ${m.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                          {formatCurrency(m.net)}
                        </TableCell>
                        <TableCell className={`numeric font-medium ${m.cash >= 0 ? "text-blue-700" : "text-rose-700"}`}>
                          {formatCurrency(m.cash)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Desglose por categoria (mismas bolsas que la pestana Real del presupuesto) */}
          {data.by_category && (
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3 bg-emerald-50">
                  <CardTitle className="text-base font-semibold text-emerald-900">
                    Entradas por categoria
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table className="table-dense">
                    <TableBody>
                      {(data.by_category.income || []).map((c) => (
                        <TableRow key={c.name}>
                          <TableCell>{c.name}</TableCell>
                          <TableCell className="numeric">{formatCurrency(c.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3 bg-rose-50">
                  <CardTitle className="text-base font-semibold text-rose-900">
                    Salidas por categoria
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <Table className="table-dense">
                    <TableBody>
                      {(data.by_category.expense || []).map((c) => (
                        <TableRow key={c.name}>
                          <TableCell>{c.name}</TableCell>
                          <TableCell className="numeric">{formatCurrency(c.total)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 8. Aging Report (new)
// ---------------------------------------------------------------------------

function AgingReport() {
  const [type, setType] = useState("receivable");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API}/reports/aging?type=${type}`);
      if (res.ok) {
        setData(await res.json());
      } else {
        const err = await res.json().catch(() => ({}));
        setError(err.detail || "Error al cargar aging");
        toast.error(err.detail || "Error al cargar aging");
      }
    } catch {
      setError("Error de conexion");
      toast.error("Error al cargar aging");
    } finally {
      setLoading(false);
    }
  }, [type]);

  const bucketLabels = ["0-30 dias", "31-60 dias", "61-90 dias", "90+ dias"];
  const bucketKeys = ["bucket_0_30", "bucket_31_60", "bucket_61_90", "bucket_90_plus"];

  // Calculate totals per bucket
  const totals = useMemo(() => {
    if (!data?.items) return null;
    return bucketKeys.map((key) =>
      data.items.reduce((sum, item) => sum + (item[key] ?? 0), 0)
    );
  }, [data]);

  const grandTotal = useMemo(() => {
    if (!totals) return 0;
    return totals.reduce((sum, t) => sum + t, 0);
  }, [totals]);

  return (
    <div className="space-y-4">
      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-64">
              <Label className="mb-2 block">Tipo</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="receivable">Cuentas a cobrar</SelectItem>
                  <SelectItem value="payable">Cuentas a pagar</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={fetchData} disabled={loading}>
              {loading ? "Cargando..." : "Consultar"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <ExcelDownloadButton />
      </div>

      {/* Results */}
      {loading && <LoadingSpinner />}
      {error && !loading && (
        <Card>
          <CardContent className="p-6 text-center text-rose-600">{error}</CardContent>
        </Card>
      )}
      {data && !loading && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-semibold">
              {type === "receivable" ? "Antiguedad de cuentas a cobrar" : "Antiguedad de cuentas a pagar"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente / Proveedor</TableHead>
                  {bucketLabels.map((label) => (
                    <TableHead key={label} className="text-right">{label}</TableHead>
                  ))}
                  <TableHead className="text-right">Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data.items || data.items.length === 0) ? (
                  <EmptyState message="Sin saldos pendientes" colSpan={6} />
                ) : (
                  <>
                    {data.items.map((item, idx) => {
                      const rowTotal = bucketKeys.reduce((sum, key) => sum + (item[key] ?? 0), 0);
                      return (
                        <TableRow key={item.entity_id || item.code || idx}>
                          <TableCell>
                            {item.code && (
                              <span className="font-mono text-blue-600 mr-2">{item.code}</span>
                            )}
                            {item.name || item.entity_name || "-"}
                          </TableCell>
                          {bucketKeys.map((key) => (
                            <TableCell key={key} className="numeric">
                              {(item[key] ?? 0) > 0 ? formatCurrency(item[key]) : "-"}
                            </TableCell>
                          ))}
                          <TableCell className="numeric font-medium">
                            {formatCurrency(rowTotal)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {/* Totals row */}
                    <TableRow className="bg-slate-100 font-medium">
                      <TableCell>Totales</TableCell>
                      {totals.map((t, idx) => (
                        <TableCell key={idx} className="numeric">
                          {formatCurrency(t)}
                        </TableCell>
                      ))}
                      <TableCell className="numeric text-lg">
                        {formatCurrency(grandTotal)}
                      </TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Divisa sin conciliar: en su moneda, senalizada, fuera de los totales EUR */}
      {data && !loading && data.foreign_pending?.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-3 bg-amber-50">
            <CardTitle className="text-base font-semibold text-amber-900">
              Pendientes en divisa (sin conciliar)
            </CardTitle>
            <p className="text-xs text-amber-800 mt-1">
              El EUR real solo se conoce tras la conciliacion bancaria: se muestran en su
              divisa original y NO suman en los totales EUR de arriba.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>{type === "receivable" ? "Cliente" : "Proveedor"}</TableHead>
                  <TableHead>Factura</TableHead>
                  <TableHead>Vencimiento</TableHead>
                  <TableHead className="text-right">Dias vencida</TableHead>
                  <TableHead className="text-right">Pendiente</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.foreign_pending.map((item, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell className="font-mono text-sm">{item.invoice_number}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatDate(item.due_date)}</TableCell>
                    <TableCell className="numeric">
                      {item.days_overdue > 0 ? item.days_overdue : "-"}
                    </TableCell>
                    <TableCell className="numeric font-medium">
                      {item.pending.toLocaleString("es-ES")} {item.currency}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 9. Monthly Close (Cierre mensual narrado) — Capa 3 · ítem 3.2
// ---------------------------------------------------------------------------
// Las cifras vienen calculadas por el backend (utils/monthly_report.py) y la
// narrativa la redacta la IA ALREDEDOR de esas cifras (validador anti-invencion
// en el backend). Aqui solo se pinta: narrativa + cifras clave de apoyo.

// Etiquetas (castellano) y unidades: @/utils/monthlyReportFormat es la única
// fuente (smoke 30-08-2026, B4: antes se pintaban las claves en crudo y todo
// número como euros).

const isPlainObject = (v) => v != null && typeof v === "object" && !Array.isArray(v);

function MonthlyMetricChips({ obj }) {
  const entries = Object.entries(obj ?? {}).filter(
    ([k, v]) => !HIDDEN_METRIC_KEYS.has(k) && v != null && typeof v !== "object"
  );
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {entries.map(([key, value]) => (
        <div
          key={key}
          className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 min-w-0"
        >
          <p className="text-[11px] uppercase tracking-wide text-slate-500">
            {labelForMetricKey(key)}
          </p>
          <p className="font-mono text-sm font-medium text-slate-900">
            {formatMetricValue(key, value)}
          </p>
        </div>
      ))}
    </div>
  );
}

// Objeto de objetos (p.ej. delta: {ingresos: {eur, pct}, gastos: {...}}) ->
// tabla con una fila por clave y una columna por subclave.
function MonthlyNestedTable({ obj }) {
  const rowKeys = Object.keys(obj ?? {}).filter((k) => !HIDDEN_METRIC_KEYS.has(k));
  const cols = [];
  for (const k of rowKeys) {
    for (const c of Object.keys(obj[k] ?? {})) {
      if (!HIDDEN_METRIC_KEYS.has(c) && !cols.includes(c)) cols.push(c);
    }
  }
  if (rowKeys.length === 0 || cols.length === 0) return null;
  return (
    <div className="overflow-x-auto">
      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead />
            {cols.map((c) => (
              <TableHead key={c} className="text-right">
                {labelForMetricKey(c)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rowKeys.map((k) => (
            <TableRow key={k}>
              <TableCell className="font-medium">{labelForMetricKey(k)}</TableCell>
              {cols.map((c) => (
                <TableCell key={c} className="numeric">
                  {formatMetricValue(c, obj[k]?.[c])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

const SEVERITY_CLASSES = {
  critical: "bg-rose-100 text-rose-800 border-rose-200",
  important: "bg-amber-100 text-amber-800 border-amber-200",
  informative: "bg-sky-100 text-sky-800 border-sky-200",
};

// Tensiones del bloque «Que vigilar»: gravedad + alerta (con su detalle) +
// valor actual y umbral de alerta con la unidad de cada detector (meses, %,
// puntos, dias, EUR) en vez de todo en euros.
function TensionsTable({ tensiones }) {
  if (!Array.isArray(tensiones) || tensiones.length === 0) {
    return <p className="text-sm text-slate-500 italic">Sin tensiones activas.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            <TableHead>{labelForMetricKey("severity")}</TableHead>
            <TableHead>{labelForMetricKey("title")}</TableHead>
            <TableHead className="text-right">{labelForMetricKey("metric_value")}</TableHead>
            <TableHead className="text-right">{labelForMetricKey("threshold")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {tensiones.map((t, idx) => (
            <TableRow key={t.tension_id ?? idx}>
              <TableCell>
                <span
                  className={`inline-block rounded border px-1.5 py-0.5 text-[11px] font-medium ${
                    SEVERITY_CLASSES[t.severity] ?? "bg-slate-100 text-slate-700 border-slate-200"
                  }`}
                >
                  {severityLabel(t.severity)}
                </span>
              </TableCell>
              <TableCell>
                <p className="font-medium text-slate-900">{t.title}</p>
                {t.detail ? <p className="text-xs text-slate-500">{t.detail}</p> : null}
              </TableCell>
              <TableCell className="numeric">
                {formatTensionMetric(t.tension_id, "value", t.metric_value)}
              </TableCell>
              <TableCell className="numeric">
                {formatTensionMetric(t.tension_id, "threshold", t.threshold)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function MonthlyMetricTable({ rows }) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  // Lista de escalares (p.ej. tensiones como strings) -> lista simple
  if (rows.some((r) => r == null || typeof r !== "object")) {
    return (
      <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700">
        {rows.map((r, idx) => (
          <li key={idx}>{typeof r === "object" && r != null ? JSON.stringify(r) : String(r)}</li>
        ))}
      </ul>
    );
  }
  // Columnas: union de claves escalares de todas las filas (sin ids internos)
  const cols = [];
  for (const row of rows) {
    for (const [k, v] of Object.entries(row)) {
      if (HIDDEN_METRIC_KEYS.has(k)) continue;
      if (v != null && typeof v === "object") continue;
      if (!cols.includes(k)) cols.push(k);
    }
  }
  if (cols.length === 0) return null;
  const isNumericCol = (c) => rows.some((r) => typeof r[c] === "number");
  return (
    <div className="overflow-x-auto">
      <Table className="table-dense">
        <TableHeader>
          <TableRow>
            {cols.map((c) => (
              <TableHead key={c} className={isNumericCol(c) ? "text-right" : ""}>
                {labelForMetricKey(c)}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, idx) => (
            <TableRow key={idx}>
              {cols.map((c) => (
                <TableCell key={c} className={typeof row[c] === "number" ? "numeric" : ""}>
                  {row[c] == null ? "-" : formatMetricValue(c, row[c])}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Render generico y defensivo de un bloque de cifras del backend:
// escalares -> chips; arrays -> tabla (tensiones -> tabla propia con unidades);
// objeto de objetos -> tabla anidada; otros objetos -> sub-bloque de chips.
export function MonthlyDataBlock({ block }) {
  if (block == null) return null;
  if (Array.isArray(block)) return <MonthlyMetricTable rows={block} />;
  if (typeof block !== "object") return <MonthlyMetricChips obj={{ valor: block }} />;
  const nested = Object.entries(block).filter(
    ([k, v]) =>
      !HIDDEN_METRIC_KEYS.has(k) &&
      v != null &&
      typeof v === "object" &&
      (!Array.isArray(v) || v.length > 0)
  );
  const renderNested = (key, value) => {
    if (key === "tensiones") return <TensionsTable tensiones={value} />;
    if (Array.isArray(value)) return <MonthlyMetricTable rows={value} />;
    const inner = Object.values(value);
    if (inner.length > 0 && inner.every(isPlainObject)) return <MonthlyNestedTable obj={value} />;
    return <MonthlyMetricChips obj={value} />;
  };
  return (
    <div className="space-y-3 min-w-0">
      <MonthlyMetricChips obj={block} />
      {nested.map(([key, value]) => (
        <div key={key} className="space-y-1.5 min-w-0">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
            {labelForMetricKey(key)}
          </p>
          {renderNested(key, value)}
        </div>
      ))}
    </div>
  );
}

function MonthlySectionCard({ title, narrativeText, children, testid }) {
  return (
    <Card data-testid={testid}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {narrativeText ? (
          <p className="text-sm text-slate-700 whitespace-pre-wrap">{narrativeText}</p>
        ) : null}
        {children}
      </CardContent>
    </Card>
  );
}

function MonthlyCloseReport() {
  // Por defecto, el ultimo mes cerrado (el anterior al actual)
  const [month, setMonth] = useState(() =>
    previousPeriod(new Date().toISOString().slice(0, 7))
  );
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null); // { type: "unreliable" | "generic", detail }

  const fetchReport = useCallback(
    async (regenerate = false) => {
      if (!month) return;
      setLoading(true);
      setError(null);
      try {
        const url = `${API}/reports/monthly-close/${month}${regenerate ? "?regenerate=true" : ""}`;
        const res = await apiFetch(url);
        if (res.ok) {
          setReport(await res.json());
        } else {
          const err = await res.json().catch(() => ({}));
          const detail = typeof err.detail === "string" ? err.detail : "";
          setReport(null);
          if (res.status === 502) {
            setError({
              type: "unreliable",
              detail: detail || "La narrativa generada no supero la validacion de cifras.",
            });
          } else {
            setError({ type: "generic", detail: detail || "Error al cargar el cierre mensual" });
            toast.error(detail || "Error al cargar el cierre mensual");
          }
        }
      } catch (e) {
        if (e?.message === "Not authenticated") return;
        setReport(null);
        setError({ type: "generic", detail: "Error de conexion" });
        toast.error("Error al cargar el cierre mensual");
      } finally {
        setLoading(false);
      }
    },
    [month]
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await apiFetch(`${API}/reports/monthly-close/${month}/pdf`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const detail = typeof err.detail === "string" ? err.detail : "";
        toast.error(detail || "No se pudo descargar el PDF del cierre mensual");
        return;
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `cierre_mensual_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el PDF del cierre mensual");
    } finally {
      setDownloading(false);
    }
  };

  const handleRegenerate = () => {
    const ok = window.confirm(
      "Regenerar el informe descartara la version guardada y volvera a generar la narrativa con IA. Continuar?"
    );
    if (ok) fetchReport(true);
  };

  const narrative = report?.narrative;
  const data = report?.data;

  return (
    <div className="space-y-4">
      {/* Selector de mes + acciones */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="w-44 max-w-full">
              <Label className="mb-2 block">Mes</Label>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="monthly-close-month"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={downloadPdf}
                disabled={loading || downloading || !month || !narrative}
                data-testid="monthly-close-pdf"
              >
                <Download className="w-4 h-4" />
                {downloading ? "Descargando..." : "Descargar PDF"}
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleRegenerate}
                disabled={loading || downloading || !month || report?.consent === false}
                data-testid="monthly-close-regenerate"
              >
                <RefreshCw className="w-4 h-4" />
                Regenerar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading && <LoadingSpinner />}

      {/* 502: narrativa no fiable — mensaje honesto, no se muestra el informe */}
      {error?.type === "unreliable" && !loading && (
        <Card className="border-amber-200" data-testid="monthly-close-unreliable">
          <CardContent className="p-6 space-y-2">
            <p className="font-medium text-amber-900">
              La narrativa generada no supero la validacion de cifras
            </p>
            <p className="text-sm text-amber-800 whitespace-pre-wrap">{error.detail}</p>
            <p className="text-sm text-slate-600">
              No se muestra un informe con cifras no verificadas. Puedes intentarlo de nuevo
              con «Regenerar»; tus cifras reales siguen disponibles en el resto de pestanas.
            </p>
          </CardContent>
        </Card>
      )}

      {error?.type === "generic" && !loading && (
        <Card>
          <CardContent className="p-6 text-center text-rose-600">{error.detail}</CardContent>
        </Card>
      )}

      {/* Sin consentimiento IA: card informativa, NO un error */}
      {report && !loading && !narrative && (
        <Card className="border-blue-200 bg-blue-50/50" data-testid="monthly-close-no-consent">
          <CardContent className="p-6 space-y-3">
            <p className="text-sm text-blue-900">
              {report.message ||
                "El informe narrado necesita la IA activada en Ajustes. Tus cifras siguen disponibles en las pestanas de Informes."}
            </p>
            <Link
              to="/ajustes"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-700 hover:underline"
            >
              Ir a Ajustes
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Informe completo */}
      {report && !loading && narrative && (
        <div className="space-y-4" data-testid="monthly-close-content">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <span>
              Cifras calculadas por LiquidArc desde tus datos; redaccion generada por IA —
              revisala antes de compartir.
            </span>
            {report.generated_at && <span>Generado el {formatDate(report.generated_at)}.</span>}
            {report.cached && (
              <Badge variant="outline" className="text-[10px]">
                guardado
              </Badge>
            )}
          </div>

          {/* Resumen ejecutivo (destacado) */}
          <Card className="border-blue-200 bg-blue-50/50" data-testid="monthly-close-summary">
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-semibold text-blue-900 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Resumen ejecutivo
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">
                {narrative.resumen_ejecutivo}
              </p>
            </CardContent>
          </Card>

          <MonthlySectionCard
            title="Resultado del mes"
            narrativeText={narrative.resultado_mes}
            testid="monthly-close-resultado"
          >
            <MonthlyDataBlock block={data?.resultado_mes} />
            {data?.presupuesto_vs_real != null ? (
              <div className="pt-3 border-t border-slate-100 space-y-2">
                <p className="text-sm font-medium text-slate-600">Presupuesto vs real</p>
                <MonthlyDataBlock block={data.presupuesto_vs_real} />
              </div>
            ) : (
              <p className="text-xs text-slate-500 italic">
                Sin presupuesto configurado para este mes.
              </p>
            )}
          </MonthlySectionCard>

          <MonthlySectionCard
            title="Tesoreria y prevision"
            narrativeText={narrative.tesoreria}
            testid="monthly-close-tesoreria"
          >
            <MonthlyDataBlock block={data?.tesoreria} />
          </MonthlySectionCard>

          <MonthlySectionCard
            title="Cobros pendientes"
            narrativeText={narrative.cobros}
            testid="monthly-close-cobros"
          >
            <MonthlyDataBlock block={data?.cobros_pendientes} />
          </MonthlySectionCard>

          <MonthlySectionCard
            title="Margenes"
            narrativeText={narrative.margenes}
            testid="monthly-close-margenes"
          >
            <MonthlyDataBlock block={data?.margenes} />
          </MonthlySectionCard>

          <MonthlySectionCard
            title="Que vigilar el mes que viene"
            narrativeText={narrative.vigilar}
            testid="monthly-close-vigilar"
          >
            <MonthlyDataBlock block={data?.vigilar} />
          </MonthlySectionCard>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Reports page
// ---------------------------------------------------------------------------

export default function Reports() {
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [activeTab, setActiveTab] = useState("balance");
  const [balanceData, setBalanceData] = useState(null);
  const [plData, setPlData] = useState(null);
  const [trialData, setTrialData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [balanceRes, plRes, trialRes] = await Promise.all([
        apiFetch(`${API}/reports/balance?period=${period}`),
        apiFetch(`${API}/reports/profit-loss?period=${period}`),
        apiFetch(`${API}/reports/trial-balance?period=${period}`),
      ]);

      if (balanceRes.ok) setBalanceData(await balanceRes.json());
      if (plRes.ok) setPlData(await plRes.json());
      if (trialRes.ok) setTrialData(await trialRes.json());
    } catch (error) {
      console.error("Error fetching reports:", error);
      toast.error("Error al cargar informes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [period]);

  // Determine if the active tab is one of the 3 original tabs that use the shared period
  const isOriginalTab = ["balance", "pl", "trial"].includes(activeTab);

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">
            Informes Financieros
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Estados financieros y análisis
          </p>
        </div>
      </div>

      {/* Period Selector — only for the original 3 tabs */}
      {isOriginalTab && (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <div className="w-48 max-w-full">
                <Label className="mb-2 block">Periodo</Label>
                <Input
                  type="month"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                  data-testid="report-period-input"
                />
              </div>
              <Button onClick={fetchReports} disabled={loading} data-testid="generate-reports-btn">
                {loading ? "Generando..." : "Generar Informes"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Reports Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="balance" className="gap-2" data-testid="tab-balance">
            <Scale className="w-4 h-4" />
            Balance
          </TabsTrigger>
          <TabsTrigger value="pl" className="gap-2" data-testid="tab-pl">
            <BarChart3 className="w-4 h-4" />
            PyG
          </TabsTrigger>
          <TabsTrigger value="monthly" className="gap-2" data-testid="tab-monthly">
            <FileText className="w-4 h-4" />
            Cierre mensual
          </TabsTrigger>
          <TabsTrigger value="trial" className="gap-2" data-testid="tab-trial">
            <BarChart3 className="w-4 h-4" />
            Sumas y Saldos
          </TabsTrigger>
          <TabsTrigger value="comparative" className="gap-2" data-testid="tab-comparative">
            <ArrowLeftRight className="w-4 h-4" />
            Comparativa
          </TabsTrigger>
          <TabsTrigger value="ledger" className="gap-2" data-testid="tab-ledger">
            <BookOpen className="w-4 h-4" />
            Libro Mayor
          </TabsTrigger>
          <TabsTrigger value="vat" className="gap-2" data-testid="tab-vat">
            <Receipt className="w-4 h-4" />
            IVA Trimestral
          </TabsTrigger>
          <TabsTrigger value="cashflow" className="gap-2" data-testid="tab-cashflow">
            <Wallet className="w-4 h-4" />
            Cash Flow
          </TabsTrigger>
          <TabsTrigger value="aging" className="gap-2" data-testid="tab-aging">
            <Clock className="w-4 h-4" />
            Aging
          </TabsTrigger>
        </TabsList>

        {/* Original 3 tabs */}
        <TabsContent value="balance" className="mt-6">
          <BalanceReport data={balanceData} loading={loading} />
        </TabsContent>

        <TabsContent value="pl" className="mt-6">
          <PLReport data={plData} loading={loading} />
        </TabsContent>

        <TabsContent value="trial" className="mt-6">
          <TrialBalanceReport data={trialData} loading={loading} />
        </TabsContent>

        {/* Cierre mensual narrado (Capa 3 · 3.2) */}
        <TabsContent value="monthly" className="mt-6">
          <MonthlyCloseReport />
        </TabsContent>

        {/* New 5 tabs */}
        <TabsContent value="comparative" className="mt-6">
          <ComparativeReport defaultPeriod={period} />
        </TabsContent>

        <TabsContent value="ledger" className="mt-6">
          <LedgerReport defaultPeriod={period} />
        </TabsContent>

        <TabsContent value="vat" className="mt-6">
          <VATReport />
        </TabsContent>

        <TabsContent value="cashflow" className="mt-6">
          <CashFlowReport />
        </TabsContent>

        <TabsContent value="aging" className="mt-6">
          <AgingReport />
        </TabsContent>
      </Tabs>
    </div>
  );
}
